import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

/** The class every block inside a rule carries; the stylesheet draws the gutter from it. */
export const RULE_BLOCK_CLASS = 'ds-rule';
/** Set on the FIRST block of a run, and holds the text the label shows. */
export const RULE_LABEL_ATTRIBUTE = 'data-ds-rule-label';

export interface RuleDecorationOptions {
	/** What to write on the label, or undefined for a rule nothing defines. */
	labelFor?: (ruleId: string) => string | undefined;
}

const ruleOf = (node: ProseMirrorNode): string | null => {
	const rule: unknown = node.attrs['rule'];
	return typeof rule === 'string' && rule !== '' ? rule : null;
};

/**
 * Collects a decoration per ruled block, labelling only the first of each run.
 *
 * Walked parent by parent rather than with `descendants`, because "first of a run" is a question
 * about SIBLINGS: a three-paragraph rule should be labelled once, at the top, the way the author
 * wrote it — labelling all three reads as three separate rules.
 */
const collect = (
	parent: ProseMirrorNode,
	contentStart: number,
	labelFor: (ruleId: string) => string | undefined,
	into: Decoration[]
): void => {
	let previousRule: string | null = null;
	parent.forEach((child, offset) => {
		const pos = contentStart + offset;
		const rule = ruleOf(child);
		if (rule !== null) {
			into.push(
				Decoration.node(pos, pos + child.nodeSize, {
					class: RULE_BLOCK_CLASS,
					...(rule === previousRule
						? {}
						: {
								[RULE_LABEL_ATTRIBUTE]: labelFor(rule) ?? 'Rule no longer exists',
							}),
				})
			);
		}
		previousRule = rule;
		if (child.isTextblock || child.isLeaf) {
			return;
		}
		collect(child, pos + 1, labelFor, into);
	});
};

/**
 * Draws the rule a block belongs to, in the page, while it is being edited.
 *
 * A condition the author cannot see is Word's mail-merge field-code problem: the logic is real,
 * it changes the document, and it is invisible unless you know a key combination. The gutter and
 * the label are the whole reason this is legible at all, so they are always on rather than
 * behind a toggle.
 *
 * `labelFor` is read at decoration time, not captured, because the rules live in React state
 * outside the document — renaming one has to show up without rebuilding the editor.
 */
export const RuleDecorations = Extension.create<RuleDecorationOptions>({
	name: 'ruleDecorations',

	addOptions() {
		return {};
	},

	addProseMirrorPlugins() {
		const optionsRef = this.options;
		return [
			new Plugin({
				key: new PluginKey('ruleDecorations'),
				props: {
					decorations: (state) => {
						const decorations: Decoration[] = [];
						collect(
							state.doc,
							0,
							(ruleId) => optionsRef.labelFor?.(ruleId),
							decorations
						);
						return DecorationSet.create(state.doc, decorations);
					},
				},
			}),
		];
	},
});
